import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import AvailabilityCard from "app/components/AllAvailability/AvailabilityCard";
import FareharborAvailabilityCard from "app/components/AllAvailability/FareharborAvailabilityCard";
import { useDeleteAvailabilityMutation } from "lib/features/availability/availabilityService";
import { useLazyGetMyBookingsQuery } from "lib/features/booking/bookingService";
import {
  useLazyGetBusinessItemsForMonthQuery,
  useLazyGetMyItemsAvailableByDateQuery,
  useLazyGetMyItemsNamesQuery,
} from "lib/features/item/itemService";
import { Item } from "lib/features/item/types";
import {
  resetIsExistingBookingLink,
  selectCurrentChatType,
  selectCustomerDetails,
  selectIsExistingBookingLink
} from "lib/features/message/messageSlice";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { formatCustomerName } from "util/formatCustomerName";
import BinIcon from "../../../assets/icons/delete_referral.svg";

interface Category {
  id: number;
  name: string;
}

interface MonthData {
  [date: string]: Item[];
}

interface SelectedAvailability {
  itemId: number;
  availabilityId: string;
  item: Item;
  availability: any;
}

interface DeletePopupProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedCount: number;
  isDeleting?: boolean;
}

interface SuccessPopupProps {
  open: boolean;
  onClose: () => void;
  deletedCount: number;
}

const SkeletonSlot = () => {
  return (
    <Box
      sx={{
        backgroundColor: '#f0f0f0',
        borderRadius: '4px',
        p: '4px 6px',
        mb: 0.5,
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '100%',
          height: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
          animation: 'shimmer 1.5s infinite',
        },
        '@keyframes shimmer': {
          '0%': { left: '-100%' },
          '100%': { left: '100%' },
        }
      }}
    >
      {/* Time skeleton */}
      <Box
        sx={{
          width: '60%',
          height: '14px',
          backgroundColor: '#e0e0e0',
          borderRadius: '2px',
          mb: 0.5
        }}
      />
      {/* Info skeleton */}
      <Box
        sx={{
          width: '80%',
          height: '11px',
          backgroundColor: '#e0e0e0',
          borderRadius: '2px'
        }}
      />
    </Box>
  );
};

const AllAvailabilities = () => {
  const theme = useTheme();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(location.search);
  const dateParam = queryParams.get("date");
  const isScreenBreak = useMediaQuery("(max-width: 842px)");

  const isExistingBookingLink = useSelector(selectIsExistingBookingLink);
  const currentChatType = useSelector(selectCurrentChatType);
  const customerDetails = useSelector(selectCustomerDetails);

  // Reset isExistingBookingLink when navigating from navbar (not from chat flow)
  useEffect(() => {
    // Only keep isExistingBookingLink if explicitly coming from chat
    const fromChat = (location.state as any)?.fromChat;
    if (!fromChat) {
      dispatch(resetIsExistingBookingLink());
    }
  }, [location.state, dispatch]);

  const [allItems, setAllItems] = useState<Item[]>([]);
  const [monthData, setMonthData] = useState<MonthData>({});
  const [selectedDate, setSelectedDate] = useState<Date>(
    dateParam ? new Date(dateParam) : new Date()
  );
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [filteredItems, setFilteredItems] = useState<Item[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [businessNames, setBusinessNames] = useState<string[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<string>("All");
  const [isFetchingAvailabilities, setIsFetchingAvailabilities] = useState<boolean>(false);
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(false);
  const [selectedDateForFullView, setSelectedDateForFullView] = useState<string | null>(null);
  const [isFullAvailabilityModalOpen, setIsFullAvailabilityModalOpen] = useState<boolean>(false);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);
  const [bookingStats, setBookingStats] = useState<{ [key: string]: { [key: string]: number } }>({});
  const [loadingDates, setLoadingDates] = useState<Set<string>>(new Set());
  const [blockDataFetch, setBlockDataFetch] = useState<boolean>(false);

  const [selectedAvailabilities, setSelectedAvailabilities] = useState<SelectedAvailability[]>([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const [deletePopupOpen, setDeletePopupOpen] = useState<boolean>(false);
  const [successPopupOpen, setSuccessPopupOpen] = useState<boolean>(false);
  const [successfulDeletes, setSuccessfulDeletes] = useState<number>(0);

  const [getMyItemsAvailableByDate] = useLazyGetMyItemsAvailableByDateQuery();
  const [getBusinessItemsForMonth] = useLazyGetBusinessItemsForMonthQuery();
  const [getMyItemsNames] = useLazyGetMyItemsNamesQuery();
  const [getMyBookings] = useLazyGetMyBookingsQuery();
  const [deleteAvailability, { isLoading: deleteLoading }] = useDeleteAvailabilityMutation();
  const [deletedFareharborAvailabilities, setDeletedFareharborAvailabilities] = useState<Set<string>>(new Set());

  const extractBusinessNames = (items: Item[]): string[] => {
    const names = new Set<string>();

    items.forEach(item => {
      const businessName = getBusinessName(item);
      if (businessName && businessName !== 'Unknown Business') {
        names.add(businessName);
      }
    });

    return Array.from(names).sort();
  };

  const handleSelectBusiness = (businessName: string) => {
    setSelectedBusiness(businessName);

    if (businessName === "All") {
      const selectedYear = selectedDate.getFullYear();
      const selectedMonth = selectedDate.getMonth();
      const selectedDay = selectedDate.getDate();
      const selectedDateString = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
      setFilteredItems(monthData[selectedDateString] || []);
    } else {
      const selectedYear = selectedDate.getFullYear();
      const selectedMonth = selectedDate.getMonth();
      const selectedDay = selectedDate.getDate();
      const selectedDateString = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
      const dateItems = monthData[selectedDateString] || [];
      const filtered = dateItems.filter(item =>
        getBusinessName(item) === businessName
      );
      setFilteredItems(filtered);
    }
  };

  useEffect(() => {
    const handleAvailabilityChanged = () => {
      if (!blockDataFetch) {
        fetchAllAvailabilities();
        fetchItemNames();
      }
    };

    window.addEventListener('availabilityAdded', handleAvailabilityChanged);
    window.addEventListener('availabilityUpdated', handleAvailabilityChanged);

    return () => {
      window.removeEventListener('availabilityAdded', handleAvailabilityChanged);
      window.removeEventListener('availabilityUpdated', handleAvailabilityChanged);
    };
  }, [blockDataFetch]);

  useEffect(() => {
    if (location.state?.refresh && !blockDataFetch) {
      fetchAllAvailabilities();
      fetchItemNames();

      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.state, blockDataFetch]);

  const shouldShowCheckbox = (item: Item, availability: any): boolean => {
    const dateForKey = availability?.start_at ? new Date(availability.start_at) : selectedDate;
    const yearForKey = dateForKey.getFullYear();
    const monthForKey = dateForKey.getMonth();
    const dayForKey = dateForKey.getDate();
    const dateString = `${yearForKey}-${String(monthForKey + 1).padStart(2, '0')}-${String(dayForKey).padStart(2, '0')}`;
    const bookingCount = getBookingCountForItem(dateString, item.id) || 0;
    const hasZeroReservations = bookingCount === 0;

    const isFromFareHarbor = item.item_type === 4;

    return hasZeroReservations || isFromFareHarbor;
  };

  const isAvailabilitySelected = (itemId: number, availabilityId: string): boolean => {
    return selectedAvailabilities.some(
      selected => selected.itemId === itemId && selected.availabilityId === availabilityId
    );
  };

  const handleCheckboxChange = (item: Item, availability: any, isSelected: boolean) => {
    const availabilityId = availability?.pk ||
      availability?.id ||
      `${availability?.start_at}-${availability?.end_at}` ||
      'default';

    if (isSelected) {
      setSelectedAvailabilities(prev => [
        ...prev,
        { itemId: item.id, availabilityId, item, availability }
      ]);
    } else {
      setSelectedAvailabilities(prev =>
        prev.filter(
          selected => !(selected.itemId === item.id && selected.availabilityId === availabilityId)
        )
      );
    }
  };

  const handleDeleteButtonClick = () => {
    if (selectedAvailabilities.length > 0) {
      setDeletePopupOpen(true);
    }
  };

  const DeleteMultipleAvailabilityPopup = ({
    open,
    onClose,
    onConfirm,
    selectedCount,
    isDeleting = false
  }: DeletePopupProps) => {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }
        }}
      >
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 400 }}>
            You're about to delete {selectedCount} availabilities.
          </Typography>
          <Typography variant="body1">
            Proceed?
          </Typography>
        </DialogContent>

        <DialogActions sx={{
          justifyContent: 'center',
          gap: 2,
          pb: 3,
          px: 3
        }}>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={isDeleting}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 5,
              minWidth: 100
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            disabled={isDeleting}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              minWidth: 100
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const DeleteSuccessPopup = ({
    open,
    onClose,
    deletedCount
  }: SuccessPopupProps) => {
    const handleClose = async () => {
      onClose();
      setBlockDataFetch(false);
    };

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ textAlign: 'center', py: '48px' }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {deletedCount} Availabilities has been deleted.
          </Typography>
        </DialogContent>
      </Dialog>
    );
  };
  const handleBulkDelete = async () => {
    if (selectedAvailabilities.length === 0) return;

    setIsBulkDeleting(true);
    setDeletePopupOpen(false);
    setBlockDataFetch(true);
    setSnackbar({
      open: true,
      message: 'Deleting availabilities...',
      severity: 'success'
    });

    try {
      const deletePromises = selectedAvailabilities.map(async (selected) => {
        try {
          if (selected.item.item_type === 1 || selected.item.item_type === 3) {
            const availabilityId = selected.availability?.id;
            if (availabilityId) {
              await deleteAvailability(availabilityId).unwrap();
              return { success: true, selected, type: 'api' };
            }
          } else if (selected.item.item_type === 4) {
            // For FareHarbor items, add to deleted list instead of API call
            const fareharborId = selected.availability?.pk || selected.availability?.id;
            if (fareharborId) {
              return { success: true, selected, type: 'fareharbor', id: String(fareharborId) };
            }
          }
          return { success: false, selected };
        } catch (error) {
          console.error(`Error deleting availability ${selected.availability?.id}:`, error);
          return { success: false, selected };
        }
      });

      const results = await Promise.allSettled(deletePromises);
      const successfulDeletes = results.filter(result =>
        result.status === 'fulfilled' && result.value?.success
      );

      const successfulDeletesCount = successfulDeletes.length;
      setSuccessfulDeletes(successfulDeletesCount);
      setSuccessPopupOpen(true);

      // Collect all deleted IDs (both API and FareHarbor)
      const deletedItemIds = new Set<string>();
      const deletedFareharborIds = new Set<string>();

      successfulDeletes.forEach(result => {
        if (result.status === 'fulfilled') {
          const value = result.value;
          const id = String(value.selected.availabilityId);
          deletedItemIds.add(id);

          if (value.type === 'fareharbor' && value.id) {
            deletedFareharborIds.add(value.id);
          }
        }
      });

      // Helper function to filter availabilities
      const filterAvailabilities = (availabilities: any[]) =>
        (availabilities || []).filter(avail => {
          const fhId = String(avail.pk ?? avail.id);
          return !deletedFareharborIds.has(fhId);
        });

      // Update all items at once
      const updateItems = (items: any[]) =>
        items.map(item => ({
          ...item,
          fareharbor_availabilities: filterAvailabilities(item.fareharbor_availabilities),
        }));

      setAllItems(prev => updateItems(prev));
      setFilteredItems(prev => updateItems(prev));
      setMonthData(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(dateKey => {
          updated[dateKey] = updateItems(updated[dateKey]);
        });
        return updated;
      });

      // Update deleted FareHarbor availabilities state
      const newDeletedSet = new Set(deletedFareharborAvailabilities);
      deletedFareharborIds.forEach(id => newDeletedSet.add(id));
      setDeletedFareharborAvailabilities(newDeletedSet);

      // Persist to localStorage
      localStorage.setItem(
        'deletedFareharborAvailabilities',
        JSON.stringify(Array.from(newDeletedSet))
      );

      setSelectedAvailabilities([]);

    } catch (error) {
      console.error("Bulk delete error:", error);
      setSnackbar({
        open: true,
        message: 'Error deleting availabilities',
        severity: 'error'
      });
      setBlockDataFetch(false);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleSlotClick = (item: Item, availability: any) => {
    setSelectedSlot({ item, availability });
    setIsDetailModalOpen(true);
  };

  const handleViewMoreClick = (dateKey: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDateForFullView(dateKey);
    setIsFullAvailabilityModalOpen(true);
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const getBusinessColor = () => {
    return { bg: "#A8E6CF80", text: "#0E4D2E" };
  };

  const businessColors = [
    { bg: "#FFB59C80", text: "#0E4D2E", border: "2px solid #E3967C" },
    { bg: "#B2F7EF80", text: "#0E4D2E", border: "2px solid #7FE3DA" },
    { bg: "#C7F46480", text: "#0E4D2E", border: "2px solid #A3DA40" },
    { bg: "#BEE5A080", text: "#0E4D2E", border: "2px solid #A0C884" },
    { bg: "#6FCF9780", text: "#0E4D2E", border: "2px solid #54B67C" },
    { bg: "#A8E6CF80", text: "#0E4D2E", border: "2px solid #83D5B6" },
    { bg: "#CDB4DB80", text: "#0E4D2E", border: "2px solid #A78EB8" },
    { bg: "#A2D2FF80", text: "#0E4D2E", border: "2px solid #7CB8F2" },
    { bg: "#B0BEC580", text: "#0E4D2E", border: "2px solid #8E9EA6" },
  ];

  const getBusinessColorByName = (name: string | undefined | null) => {
    if (!name) {
      return { bg: '#f5f5f5', text: '#666', border: "" };
    }
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return businessColors[hash % businessColors.length];
  };

  const formatTimeShort = (dateString: string) => {
    try {
      return moment.utc(dateString).tz("America/New_York").format("h:mm A");
    } catch (error) {
      console.error("Error formatting time:", error);
      return dateString;
    }
  };

  const getDuration = (startTime: string, endTime: string): string => {
    try {
      const start = moment.utc(startTime).tz("America/New_York");
      const end = moment.utc(endTime).tz("America/New_York");

      let duration = moment.duration(end.diff(start)).asHours();

      if (duration < 0) {
        const correctedEnd = moment.utc(endTime).tz("America/New_York").add(1, "day");
        duration = moment.duration(correctedEnd.diff(start)).asHours();
      }

      if (duration > 12) duration -= 12;

      return `${duration} hr`;
    } catch (error) {
      console.error("Error calculating duration:", error);
      return "";
    }
  };

  const getBusinessName = (item: Item): string => {
    if (item.item_type === 4 && item.farehabor_item_details?.name) {
      return item.farehabor_item_details.name;
    } else if (item.itemDetails?.[0]?.item_name) {
      return item.itemDetails[0].item_name;
    } else if (item.business?.name) {
      return item.business.name;
    }
    return 'Unknown Business';
  };

  const getAllSlotsForDate = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    let dayItems = monthData[dateString] || [];

    if (selectedCategory && selectedCategory.id !== 0) {
      dayItems = dayItems.filter(item =>
        item?.itemDetails?.[0]?.item_name === selectedCategory.name ||
        item?.farehabor_item_details?.name === selectedCategory.name
      );
    }
    console.log("all items: ", dayItems)

    if (selectedBusiness && selectedBusiness !== "All" && selectedBusiness !== "Direct") {
      dayItems = dayItems.filter(item =>
        getBusinessName(item) === selectedBusiness
      );
    } else if (selectedBusiness && selectedBusiness === "Direct") {
      dayItems = dayItems.filter(item =>
        item.availabilities.length > 0 && item.availabilities.some(avail => avail.private === true)
      );
    }

    const allSlots: any[] = [];

    dayItems.forEach((item) => {
      const fhAvailabilities = item.fareharbor_availabilities || [];
      const normalAvailabilities = item.availabilities || [];

      if (fhAvailabilities.length > 0) {
        fhAvailabilities.forEach((avail) => allSlots.push({ item, availability: avail }));
      } else if ((item.item_type === 1 || item.item_type === 3) && normalAvailabilities.length > 0) {
        normalAvailabilities.forEach((avail) => allSlots.push({ item, availability: avail }));
      } else if (item.item_type === 1 || item.item_type === 3) {
        allSlots.push({ item, availability: null });
      }
    });

    return allSlots;
  };

  const getPriceForSlot = (item: Item, availability: any): number => {
    let price = 0;

    if (availability) {
      if (availability.customer_type_rates?.[0]?.customer_prototype?.total) {
        price = Number(availability.customer_type_rates[0].customer_prototype.total) / 100;
      } else if (availability.price) {
        price = Number(availability.price) / 100;
      }

      if (item?.fareharbor_availabilities && availability.custom_field_instances) {
        try {
          const extendedOption = availability.custom_field_instances.find(
            (f: any) => f.custom_field.type === "extended-option" && f.custom_field.is_required
          );
          if (extendedOption) price = extendedOption.custom_field.extended_options[0].total / 100;
        } catch { }
      }
    } else {
      price = item.price || 0;
    }

    return price;
  };

  const getAvailableCount = (item: Item, availability: any): number => {
    if (availability) return availability.capacity || availability.available || 1;
    return item.capacity || item.available || 1;
  };

  const getBookingCountForItem = (dateString: string, itemId: number): number => {
    return bookingStats[dateString]?.[itemId] || 0;
  };

  const getAvailableSpots = (item: Item, availability: any, bookingCount: number): number => {
    const totalCapacity = getAvailableCount(item, availability);
    return Math.max(0, totalCapacity - bookingCount);
  };

  const fetchBookingStats = async (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    try {
      const stats: { [key: string]: number } = {};
      const response = await getMyBookings({
        business_id: Number(id),
        date: date,
      }).unwrap();
      const bookingsArray = response?.bookings || response?.data || [];

      if (Array.isArray(bookingsArray) && bookingsArray.length > 0) {
        bookingsArray.forEach((booking: any) => {
          const itemId = booking.referral?.item_id;
          const ticketsBought = booking.tickets_bought || 1;

          if (itemId) {
            const reservationDate = new Date(booking.reservation_time);
            const resYear = reservationDate.getFullYear();
            const resMonth = reservationDate.getMonth();
            const resDay = reservationDate.getDate();
            const reservationDateString = `${resYear}-${String(resMonth + 1).padStart(2, '0')}-${String(resDay).padStart(2, '0')}`;
            if (reservationDateString === dateString) {
              stats[itemId] = (stats[itemId] || 0) + ticketsBought;
            }
          } else {
            console.warn('Booking missing item_id:', booking);
          }
        });
      }

      setBookingStats(prev => ({
        ...prev,
        [dateString]: stats
      }));
    } catch (error) {
      setBookingStats(prev => ({
        ...prev,
        [dateString]: {}
      }));
    }
  };

  const fetchAllAvailabilities = async () => {
    // Just call fetchCurrentMonthData since it now fetches all month data at once
    await fetchCurrentMonthData();
  };

  const fetchCurrentMonthData = async () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Define startDateString
    const firstDayOfMonth = new Date(year, month, 1);
    const startDateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(1).padStart(2, '0')}`;

    // Set loading for ALL dates in the month
    const allDatesInMonth = new Set<string>();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateYear = date.getFullYear();
      const dateMonth = date.getMonth();
      const dateDay = date.getDate();
      const dateString = `${dateYear}-${String(dateMonth + 1).padStart(2, '0')}-${String(dateDay).padStart(2, '0')}`;
      allDatesInMonth.add(dateString);
    }

    setIsFetchingAvailabilities(true);
    setLoadingDates(allDatesInMonth); // Show loading state for all dates

    try {
      // Single API call for the entire month
      const response = await getBusinessItemsForMonth({
        businessId: Number(id),
        startDate: startDateString,
      }).unwrap();

      const allFetchedItems = response?.items || [];

      console.log("📅 Fetching data for:", startDateString);
      console.log("📦 Total items received:", allFetchedItems);
      console.log("🔍 Sample item:", allFetchedItems[0]);

      // Organize items by date
      const monthDataObj: MonthData = {};

      allFetchedItems.forEach((item: Item) => {
        // Skip items that are marked as deleted
        if (item.record_status === false) {
          return;
        }

        // For FareHarbor items (type 4)
        if (item.item_type === 4 && item.fareharbor_availabilities && item.fareharbor_availabilities.length > 0) {
          item.fareharbor_availabilities.forEach((avail) => {
            // check if this FareHarbor availability was deleted by user on frontend
            const fareharborId = avail.pk || avail.id;
            if (fareharborId && deletedFareharborAvailabilities.has(String(fareharborId))) {
              return;
            }

            if (avail.start_at) {
              const availDate = new Date(avail.start_at);
              const availYear = availDate.getFullYear();
              const availMonth = availDate.getMonth();
              const availDay = availDate.getDate();
              const availDateString = `${availYear}-${String(availMonth + 1).padStart(2, '0')}-${String(availDay).padStart(2, '0')}`;

              if (!monthDataObj[availDateString]) {
                monthDataObj[availDateString] = [];
              }

              const itemForDate = {
                ...item,
                fareharbor_availabilities: [avail]
              };
              monthDataObj[availDateString].push(itemForDate);
            }
          });
          return;
        }

        // For regular items (types 1, 3, etc.)
        if (item.availabilities && item.availabilities.length > 0) {
          item.availabilities.forEach((avail) => {
            // IMP: Skip soft-deleted availabilities
            if (avail.record_status === false) {
              return;
            }

            if (avail.start_time) {
              const availDate = new Date(avail.start_time);
              const availYear = availDate.getFullYear();
              const availMonth = availDate.getMonth();
              const availDay = availDate.getDate();
              const availDateString = `${availYear}-${String(availMonth + 1).padStart(2, '0')}-${String(availDay).padStart(2, '0')}`;

              if (!monthDataObj[availDateString]) {
                monthDataObj[availDateString] = [];
              }

              const itemForDate = {
                ...item,
                availabilities: [avail]
              };
              monthDataObj[availDateString].push(itemForDate);
            }
          });
        }
      });

      setMonthData(monthDataObj);
      setAllItems(allFetchedItems);
      setBusinessNames(extractBusinessNames(allFetchedItems));

      console.log("📊 Organized data by dates:", monthDataObj);
      console.log("📋 Date keys:", Object.keys(monthDataObj).sort());

      // Fetch booking stats for all dates in the month
      const bookingStatsPromises = [];
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        bookingStatsPromises.push(fetchBookingStats(date));
      }
      await Promise.all(bookingStatsPromises);

      setLoadingDates(new Set());

      const selectedYear = selectedDate.getFullYear();
      const selectedMonth = selectedDate.getMonth();
      const selectedDay = selectedDate.getDate();
      const selectedKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
      if (monthDataObj[selectedKey]) {
        setFilteredItems(monthDataObj[selectedKey]);
      }

      console.log("All data loaded for month:", allFetchedItems.length, "items");

    } catch (error) {
      console.error("Error fetching current month data:", error);
      setLoadingDates(new Set());
    } finally {
      setIsFetchingAvailabilities(false);
    }
  };

  const organizeDataByDate = (items: Item[]) => {
    const organizedData: MonthData = {};

    items.forEach(item => {
      // For FareHarbor items - check ALL availabilities
      if (item.item_type === 4 && item.fareharbor_availabilities && item.fareharbor_availabilities.length > 0) {
        item.fareharbor_availabilities.forEach((avail) => {
          if (avail.start_at) {
            const availDate = new Date(avail.start_at);
            const itemYear = availDate.getFullYear();
            const itemMonth = availDate.getMonth();
            const itemDay = availDate.getDate();
            const itemDate = `${itemYear}-${String(itemMonth + 1).padStart(2, '0')}-${String(itemDay).padStart(2, '0')}`;

            if (!organizedData[itemDate]) {
              organizedData[itemDate] = [];
            }
            // Create a new item object for each availability
            const itemForDate = {
              ...item,
              fareharbor_availabilities: [avail]
            };
            organizedData[itemDate].push(itemForDate);
          }
        });
      }
      // For regular items - check ALL availabilities
      else if (item.availabilities && item.availabilities.length > 0) {
        item.availabilities.forEach((avail) => {
          if (avail.start_time) {
            const availDate = new Date(avail.start_time);
            const itemYear = availDate.getFullYear();
            const itemMonth = availDate.getMonth();
            const itemDay = availDate.getDate();
            const itemDate = `${itemYear}-${String(itemMonth + 1).padStart(2, '0')}-${String(itemDay).padStart(2, '0')}`;

            if (!organizedData[itemDate]) {
              organizedData[itemDate] = [];
            }
            // Create a new item object for each availability
            const itemForDate = {
              ...item,
              availabilities: [avail]
            };
            organizedData[itemDate].push(itemForDate);
          }
        });
      }
    });

    setMonthData(organizedData);

    const selectedYear = selectedDate.getFullYear();
    const selectedMonth = selectedDate.getMonth();
    const selectedDay = selectedDate.getDate();
    const selectedDateString = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
    setFilteredItems(organizedData[selectedDateString] || []);
  };

  const handleRemoveItem = (itemId: number) => {
    setFilteredItems((prevItems) =>
      prevItems.filter((item) => item.id !== itemId)
    );
  };

  const handleSelectCategory = (category: Category) => {
    setSelectedCategory(category);
    if (category.id === 0) {
      const selectedYear = selectedDate.getFullYear();
      const selectedMonth = selectedDate.getMonth();
      const selectedDay = selectedDate.getDate();
      const selectedDateString = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
      setFilteredItems(monthData[selectedDateString] || []);
    } else {
      const selectedYear = selectedDate.getFullYear();
      const selectedMonth = selectedDate.getMonth();
      const selectedDay = selectedDate.getDate();
      const selectedDateString = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
      const dateItems = monthData[selectedDateString] || [];
      const filtered = dateItems.filter(item =>
        item?.itemDetails?.[0]?.item_name === category.name ||
        item?.farehabor_item_details?.name === category.name
      );
      setFilteredItems(filtered);
    }
  };

  const fetchItemNames = async () => {
    try {
      setCategoriesLoading(true);
      const response = await getMyItemsNames({
        businessId: Number(id),
        startDate: `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`,
      });
      const newCategories = [
        { id: 0, name: "All" },
        ...response?.data?.categories,
      ];

      setCategories(newCategories);
      setSelectedCategory({ id: 0, name: "All" });

    } catch (error) {
      console.error("Error fetching item names:", error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newMonth = new Date(currentMonth);
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }
    setCurrentMonth(newMonth);
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;;

    let items = monthData[dateString] || [];

    if (selectedCategory && selectedCategory.id !== 0) {
      items = items.filter(item =>
        item?.itemDetails?.[0]?.item_name === selectedCategory.name ||
        item?.farehabor_item_details?.name === selectedCategory.name
      );
    }

    if (selectedBusiness && selectedBusiness !== "All") {
      items = items.filter(item =>
        getBusinessName(item) === selectedBusiness
      );
    }

    setFilteredItems(items);

    const queryParams = new URLSearchParams(location.search);
    queryParams.set("date", dateString);
    navigate(`?${queryParams.toString()}`);
  };

  const EnhancedSlotDisplay = ({ slot, dateKey }: { slot: any, dateKey: string }) => {
    const { item, availability } = slot;
    const name = item?.farehabor_item_details?.name || item?.itemDetails?.[0]?.item_name || 'Unnamed Item';
    const colors = getBusinessColorByName(name);
    const price = getPriceForSlot(item, availability);
    const bookingCount = getBookingCountForItem(dateKey, item.id);
    const availableSpots = getAvailableSpots(item, availability, bookingCount);

    const showCheckbox = shouldShowCheckbox(item, availability);
    const isSelected = isAvailabilitySelected(
      item.id,
      availability?.pk ||
      availability?.id ||
      `${availability?.start_at}-${availability?.end_at}` ||
      'default'
    );

    let timeDisplay = '';
    let durationDisplay = '';

    if (availability) {
      if (availability.start_at) {
        timeDisplay = formatTimeShort(availability.start_at);
        if (availability.end_at) durationDisplay = getDuration(availability.start_at, availability.end_at);
      } else if (availability.start_time) {
        timeDisplay = formatTimeShort(availability.start_time);
      }
    }

    const slotKey = availability
      ? `${item.id}-${availability.start_at || "noStart"}-${availability.end_at || "noEnd"}`
      : `${item.id}-no-availability`;

    return (
      <Box
        key={slotKey}
        onClick={(e) => {
          e.stopPropagation();
          handleSlotClick(item, availability);
        }}
        sx={{
          backgroundColor: availability.private ? 'rgba(0, 0, 0, 0.5)' : colors.bg,
          border: bookingCount > 0 ? colors.border : 'none',
          borderRadius: '4px',
          p: '4px 6px',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          position: 'relative',
          width: '100%',
          '&:hover': {
            transform: 'translateX(2px)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          },
        }}
      >
        {!isExistingBookingLink && showCheckbox && (
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              handleCheckboxChange(item, availability, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              color: availability.private ? '#fff' : colors.text,
              top: 2,
              right: 2,
              padding: 0,
              '& .MuiSvgIcon-root': {
                fontSize: 16
              },
              "&.Mui-checked": {
                color: "#fff",
              },
            }}
          />
        )}

        {timeDisplay && (
          <Typography display="flex" alignItems="flex-end" gap={1} component="p" sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: availability.private ? '#fff' : colors.text,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            pr: showCheckbox ? 2 : 0
          }}>
            {timeDisplay}
            <Typography sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: availability.private ? '#fff' : colors.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.2,
              mb: 0.2,
              pr: showCheckbox ? 2 : 0
            }}>
              {name.length > 16 ? `${name.substring(0, 15)}...` : name}
            </Typography>
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography sx={{
              fontSize: '11px',
              color: availability.private ? '#fff' : colors.text,
              opacity: 0.8,
              lineHeight: 1.2,
              fontWeight: bookingCount > 0 ? 600 : 400
            }}>
              {bookingCount} Booked
            </Typography>
            <Typography sx={{
              fontSize: '11px',
              color: availability.private ? '#fff' : colors.text,
              opacity: 0.8,
              lineHeight: 1.2,
              fontWeight: bookingCount > 0 ? 600 : 400
            }}>
              |
            </Typography>

            <Typography sx={{
              fontSize: '11px',
              color: availableSpots > 0 && availability.private ? '#fff' : colors.text,
              opacity: 0.8,
              lineHeight: 1.2,
              fontWeight: 400
            }}>
              {availableSpots} Available
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const CustomCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const adjustedFirstDay = (firstDayOfMonth + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];

    for (let i = 0; i < adjustedFirstDay; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    const today = new Date();
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth();
    const todayDay = today.getDate();
    const todayKey = `${todayYear}-${String(todayMonth + 1).padStart(2, '0')}-${String(todayDay).padStart(2, '0')}`;

    return (
      <Box sx={{ mb: 3 }}>
        {/* Header */}
        <Box mb="14px">
          <Typography variant="h5" mb="34px" sx={{ fontSize: { xs: "32px", md: '44px' }, fontWeight: 700 }}>
            My Calendar
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ChevronLeft
              sx={{ cursor: "pointer", fontSize: "40px" }}
              onClick={() => navigateMonth('prev')}
            />
            <Typography variant="h6" sx={{ minWidth: "160px", textAlign: "center", fontSize: { xs: "24px", md: '34px' }, fontWeight: 700 }}>
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </Typography>
            <ChevronRight
              sx={{ cursor: "pointer", fontSize: "40px" }}
              onClick={() => navigateMonth('next')}
            />
          </Box>
        </Box>

        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent='space-between'>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>

            {/* Business Filter */}
            {businessNames.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Chip
                  key="all-businesses"
                  label="All"
                  onClick={() => handleSelectBusiness("All")}
                  sx={{
                    borderRadius: "16px",
                    fontWeight: 500,
                    cursor: "pointer",

                    border: `1px solid ${selectedBusiness === "All" ? '#8D8D8D' : '#8D8D8D'}`,
                  }}
                />


                {businessNames.map((businessName) => {
                  const colors = getBusinessColorByName(businessName);
                  const isSelected = selectedBusiness === businessName;

                  return (
                    <Chip
                      key={businessName}
                      label={businessName}
                      onClick={() => handleSelectBusiness(businessName)}
                      sx={{
                        borderRadius: "16px",
                        fontWeight: 500,
                        cursor: "pointer",
                        color: isSelected ? colors.text : colors.text,
                        borderColor: colors.text,
                        backgroundColor: colors.bg,
                        border: `2px solid ${isSelected ? colors.text : colors.bg}`,
                      }}
                    />
                  );
                })}

                {
                  !isExistingBookingLink && (
                    <Chip
                      key="direct-businesses"
                      label="Direct links"
                      onClick={() => handleSelectBusiness("Direct")}
                      sx={{
                        borderRadius: "16px",
                        fontWeight: 500,
                        cursor: "pointer",
                        background: "#00000080",
                        color: "#fff",
                        border: `1px solid ${selectedBusiness === "Direct" ? '#00000080' : '#00000080'}`,
                        "&:hover": {
                          color: "#00000080",
                        },
                      }}
                    />
                  )
                }
              </Box>
            )}

          </Box>

          {/* Bulk Delete Button */}
          {
            !isExistingBookingLink && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<img src={BinIcon} width={20} height={20} />}
                  onClick={handleDeleteButtonClick}
                  disabled={selectedAvailabilities.length === 0 || isBulkDeleting}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    opacity: selectedAvailabilities.length === 0 ? 0.6 : 1,
                    borderColor: 'transparent',
                    "&:hover": {
                      borderColor: 'transparent',
                      backgroundColor: 'transparent'
                    },
                    "&:disabled": {
                      borderColor: 'transparent',
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  {isBulkDeleting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Delete Multiple Availabilities"
                  )}
                </Button>
              </Box>
            )
          }
        </Box>

        {/* Day Headers */}
        <Box sx={{ overflow: "auto" }}>


          <Box width="100%" sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0, backgroundColor: '#F6F7F9', }}>
            {daysOfWeek.map(day => (
              <Typography
                key={day}
                variant="body2"
                sx={{
                  fontWeight: 600,
                  textAlign: 'center',
                  color: '#666',
                  p: 1,
                  // border: '1px solid #e0e0e0',
                  maxWidth: '220px',
                  flexShrink: 0,
                }}
              >
                {day}
              </Typography>
            ))}
          </Box>

          {/* Calendar Grid */}
          <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0 }}>
              {days.map((day, index) => {
                if (day === null) {
                  return <Box key={`empty-${index}`} sx={{ border: '1px solid #e0e0e0' }} />;
                }

                const date = new Date(year, month, day);
                const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const isSelected = selectedDate.toDateString() === date.toDateString();
                const isToday = dateKey === todayKey;
                const allSlots = getAllSlotsForDate(date);
                console.log("total slots: ", allSlots)
                const isDateLoading = loadingDates.has(dateKey);
                return (
                  <Box
                    key={dateKey}
                    sx={{
                      border: '1px solid #e0e0e0',
                      p: 1.5,
                      backgroundColor: '#fff',
                      minHeight: '120px',
                      position: 'relative',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#fafafa'
                      },
                    }}
                    onClick={() => handleDateSelect(date)}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isToday ? 700 : 500,
                        color: isToday ? theme.palette.primary.main : '#333',
                        mb: 0.5,
                      }}
                    >
                      {day}
                    </Typography>

                    {/* Show skeleton if loading, otherwise show real data */}
                    {isDateLoading ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <SkeletonSlot />
                        <SkeletonSlot />
                        <SkeletonSlot />
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: '200px' }}>
                        {allSlots.slice(0, 4).map((slot, slotIndex) => (
                          isExistingBookingLink ? (
                            !slot.availability.private && (
                              <EnhancedSlotDisplay
                                key={`${slot.item.id}-${slotIndex}`}
                                slot={slot}
                                dateKey={dateKey}
                              />
                            )
                          ) : (
                            <EnhancedSlotDisplay
                              key={`${slot.item.id}-${slotIndex}`}
                              slot={slot}
                              dateKey={dateKey}
                            />
                          )
                        ))}

                        {allSlots.length > 4 && (
                          <Typography
                            onClick={(e) => handleViewMoreClick(dateKey, e)}
                            sx={{
                              fontSize: '0.7rem',
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                              textAlign: 'center',
                              mt: 0.5,
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            +{allSlots.length - 4} more
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  useEffect(() => {
    // load deleted FareHarbor availabilities from localStorage on mount
    const storedDeleted = localStorage.getItem('deletedFareharborAvailabilities');
    if (storedDeleted) {
      try {
        const parsedDeleted = JSON.parse(storedDeleted);
        setDeletedFareharborAvailabilities(new Set(parsedDeleted));
      } catch (error) {
        console.error('Error loading deleted FareHarbor availabilities:', error);
      }
    }
  }, []);

  useEffect(() => {
    // only fetch data after deletedFareharborAvailabilities is loaded from localStorage
    if (blockDataFetch) return;
    if (deletedFareharborAvailabilities.size >= 0) {
      fetchAllAvailabilities();
      fetchItemNames();
    }
  }, [deletedFareharborAvailabilities.size]);

  useEffect(() => {
    // only fetch data after deletedFareharborAvailabilities is loaded
    if (blockDataFetch) return;
    if (deletedFareharborAvailabilities) {
      fetchCurrentMonthData();
    }
  }, [currentMonth, deletedFareharborAvailabilities]);

  const renderFullAvailabilityModal = () => {
    if (!selectedDateForFullView) return null;

    const date = new Date(selectedDateForFullView);
    const allSlots = getAllSlotsForDate(date);
    const formattedDate = date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    return (
      <Dialog
        open={isFullAvailabilityModalOpen}
        onClose={() => setIsFullAvailabilityModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: "80vh" } }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            All Availability for {formattedDate}
          </Typography>

          {allSlots.length === 0 ? (
            <Typography sx={{ textAlign: "center", py: 4, color: "#666" }}>
              No availability found for this date.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {allSlots.map((slot: any, index: number) => {
                const { item, availability } = slot;
                const name = item?.farehabor_item_details?.name || item?.itemDetails?.[0]?.item_name || "Unnamed Item";
                const colors = getBusinessColorByName(name);
                const price = getPriceForSlot(item, availability);
                const bookingCount = getBookingCountForItem(selectedDateForFullView, item.id);
                const available = getAvailableSpots(item, availability, bookingCount);
                const showCheckbox = shouldShowCheckbox(item, availability);
                const isSelected = isAvailabilitySelected(
                  item.id,
                  availability?.pk ||
                  availability?.id ||
                  `${availability?.start_at}-${availability?.end_at}` ||
                  'default'
                );

                let timeDisplay = "";
                let durationDisplay = "";

                if (availability) {
                  if (availability.start_at) {
                    timeDisplay = formatTimeShort(availability.start_at);
                    if (availability.end_at) durationDisplay = getDuration(availability.start_at, availability.end_at);
                  } else if (availability.start_time) {
                    timeDisplay = formatTimeShort(availability.start_time);
                  }
                }

                return (
                  <Box
                    key={`full-${item.id}-${availability?.start_at || "noStart"}-${availability?.end_at || "noEnd"}-${index}`}
                    onClick={() => {
                      handleSlotClick(item, availability);
                      setIsFullAvailabilityModalOpen(false);
                    }}
                    sx={{
                      backgroundColor: colors.bg,
                      borderRadius: "6px",
                      p: 2,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      position: 'relative',
                      "&:hover": { transform: "translateX(4px)", boxShadow: "0 4px 8px rgba(0,0,0,0.15)" },
                    }}
                  >
                    {showCheckbox && (
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleCheckboxChange(item, availability, e.target.checked);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          '& .MuiSvgIcon-root': {
                            fontSize: 18
                          }
                        }}
                      />
                    )}

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", pr: showCheckbox ? 4 : 0 }}>
                      <Box>
                        {timeDisplay && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                            <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: colors.text, lineHeight: 1.2, whiteSpace: "nowrap" }}>{timeDisplay}</Typography>
                            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: colors.text, lineHeight: 1.2 }}>{name}</Typography>
                          </Box>
                        )}

                        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>

                          <Typography sx={{ fontSize: "0.75rem", color: bookingCount > 0 ? '#D32F2F' : colors.text, opacity: 0.8, lineHeight: 1.2, fontWeight: bookingCount > 0 ? 600 : 400 }}>
                            {bookingCount} Booked
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: bookingCount > 0 ? '#D32F2F' : colors.text, opacity: 0.8, lineHeight: 1.2, fontWeight: bookingCount > 0 ? 600 : 400 }}>
                            |
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: available > 0 ? '#2E7D32' : '#D32F2F', opacity: 0.8, lineHeight: 1.2, fontWeight: 600 }}>
                            {available} Available
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: { xs: "16px", md: "32px" }, minHeight: "100vh" }}>
      {isExistingBookingLink &&
        customerDetails?.customer_name &&
        !isFetchingAvailabilities &&
        !categoriesLoading && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
              Select the availability you would like to share with{" "}
              <strong>
                {formatCustomerName(customerDetails?.customer_name)}
              </strong>
            </Typography>
          </Box>
        )}

      <Box>
        <CustomCalendar />
      </Box>

      <Dialog
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxWidth: "707px", height: "492px" } }}
      >
        <Box sx={{ p: 0, maxHeight: "80vh", overflow: "auto" }}>
          {isExistingBookingLink && customerDetails?.customer_name && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
                Select the availability you would like to share with <strong>{formatCustomerName(customerDetails.customer_name)}</strong>
              </Typography>
            </Box>
          )}

          {selectedSlot ? (
            <Box>
              {selectedSlot.item.item_type === 1 && (
                <AvailabilityCard completeItem={selectedSlot.item} removeItem={() => setIsDetailModalOpen(false)} />
              )}
              {selectedSlot.item.item_type === 3 && (
                <AvailabilityCard completeItem={selectedSlot.item} removeItem={() => setIsDetailModalOpen(false)} />
              )}
              {selectedSlot.item.item_type === 4 && selectedSlot.availability && (
                <FareharborAvailabilityCard
                  key={selectedSlot.item.id}
                  item={{ ...selectedSlot.item, fareharbor_availabilities: [selectedSlot.availability] }}
                />
              )}
            </Box>
          ) : (
            <Typography sx={{ textAlign: "center", py: 4, color: "#666" }}>No availability selected.</Typography>
          )}
        </Box>
      </Dialog>

      {renderFullAvailabilityModal()}

      <DeleteMultipleAvailabilityPopup
        open={deletePopupOpen}
        onClose={() => setDeletePopupOpen(false)}
        onConfirm={handleBulkDelete}
        selectedCount={selectedAvailabilities.length}
        isDeleting={isBulkDeleting}
      />

      <DeleteSuccessPopup
        open={successPopupOpen}
        onClose={() => setSuccessPopupOpen(false)}
        deletedCount={successfulDeletes}
      />
    </Box>
  );
};

export default AllAvailabilities;